import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Check } from 'lucide-react';
import {
  CONDITION_STATUS_OPTIONS,
  CONDITION_STATE_OPTIONS,
  FLOOR_LEVEL_OPTIONS,
  FEATURE_OPTIONS,
  BEDROOM_OPTIONS,
  BATHROOM_OPTIONS,
  bedroomKeyToValue,
  bedroomValueToKey,
  bathroomKeyToValue,
  bathroomValueToKey,
} from '@/lib/propertyTaxonomy';

/**
 * Used in SubmitPropertyPage + EditPropertyPage. Reads/writes:
 *   form.bedrooms (int), form.bathrooms (int)
 *   form.condition_status, form.condition_state, form.floor_level (string)
 *   form.features (array of keys)
 */
export default function PropertyAttributesForm({ form, setForm, idPrefix = 'prop-attr' }) {
  const { language } = useLanguage();
  const tt = (en, es) => (language === 'es' ? es : en);

  const setVal = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const toggleFeature = (key) => {
    const cur = Array.isArray(form.features) ? form.features : [];
    setForm(f => ({ ...f, features: cur.includes(key) ? cur.filter(x => x !== key) : [...cur, key] }));
  };

  const bedKey = bedroomValueToKey(form.bedrooms);
  const bathKey = bathroomValueToKey(form.bathrooms);

  return (
    <Card>
      <CardContent className="pt-6 space-y-5">
        <h3 className="font-heading font-semibold text-slate-700">
          {tt('Property Attributes', 'Atributos de la Propiedad')}
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>{tt('Property Condition', 'Situación de la vivienda')}</Label>
            <Select value={form.condition_status || ''} onValueChange={v => setVal('condition_status', v)}>
              <SelectTrigger className="mt-1 h-11 bg-slate-50" data-testid={`${idPrefix}-condition-status`}>
                <SelectValue placeholder={tt('Select…', 'Selecciona…')} />
              </SelectTrigger>
              <SelectContent>
                {CONDITION_STATUS_OPTIONS.map(o => (
                  <SelectItem key={o.key} value={o.key} data-testid={`${idPrefix}-condition-status-${o.key}`}>
                    {tt(o.en, o.es)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>{tt('Condition', 'Estado')}</Label>
            <Select value={form.condition_state || ''} onValueChange={v => setVal('condition_state', v)}>
              <SelectTrigger className="mt-1 h-11 bg-slate-50" data-testid={`${idPrefix}-condition-state`}>
                <SelectValue placeholder={tt('Select…', 'Selecciona…')} />
              </SelectTrigger>
              <SelectContent>
                {CONDITION_STATE_OPTIONS.map(o => (
                  <SelectItem key={o.key} value={o.key} data-testid={`${idPrefix}-condition-state-${o.key}`}>
                    {tt(o.en, o.es)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>{tt('Bedrooms', 'Habitaciones')}</Label>
            <Select value={bedKey} onValueChange={v => setVal('bedrooms', bedroomKeyToValue(v))}>
              <SelectTrigger className="mt-1 h-11 bg-slate-50" data-testid={`${idPrefix}-bedrooms`}>
                <SelectValue placeholder={tt('Select…', 'Selecciona…')} />
              </SelectTrigger>
              <SelectContent>
                {BEDROOM_OPTIONS.map(o => (
                  <SelectItem key={o.key} value={o.key} data-testid={`${idPrefix}-bedrooms-${o.key}`}>
                    {tt(o.en, o.es)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>{tt('Bathrooms', 'Baños')}</Label>
            <Select value={bathKey} onValueChange={v => setVal('bathrooms', bathroomKeyToValue(v))}>
              <SelectTrigger className="mt-1 h-11 bg-slate-50" data-testid={`${idPrefix}-bathrooms`}>
                <SelectValue placeholder={tt('Select…', 'Selecciona…')} />
              </SelectTrigger>
              <SelectContent>
                {BATHROOM_OPTIONS.map(o => (
                  <SelectItem key={o.key} value={o.key} data-testid={`${idPrefix}-bathrooms-${o.key}`}>
                    {tt(o.en, o.es)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>{tt('Floor Level', 'Elevación')}</Label>
            <Select value={form.floor_level || ''} onValueChange={v => setVal('floor_level', v)}>
              <SelectTrigger className="mt-1 h-11 bg-slate-50" data-testid={`${idPrefix}-floor-level`}>
                <SelectValue placeholder={tt('Select…', 'Selecciona…')} />
              </SelectTrigger>
              <SelectContent>
                {FLOOR_LEVEL_OPTIONS.map(o => (
                  <SelectItem key={o.key} value={o.key} data-testid={`${idPrefix}-floor-level-${o.key}`}>
                    {tt(o.en, o.es)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="mb-2 block">{tt('Features', 'Características')}</Label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {FEATURE_OPTIONS.map(o => {
              const checked = Array.isArray(form.features) && form.features.includes(o.key);
              return (
                <button key={o.key} type="button" onClick={() => toggleFeature(o.key)}
                  data-testid={`${idPrefix}-feature-${o.key}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left transition-colors
                    ${checked ? 'bg-[#D4AF37]/10 border-[#D4AF37] text-[#0B192C] font-medium' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'}`}>
                  <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0
                    ${checked ? 'bg-[#D4AF37] border-[#D4AF37]' : 'border-slate-300 bg-white'}`}>
                    {checked && <Check className="w-3 h-3 text-[#0B192C]" />}
                  </div>
                  <span className="truncate">{tt(o.en, o.es)}</span>
                </button>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
